import { type Dispatch, type SetStateAction } from 'react'

import { getCurrency } from '@common/utils/currency'
import type { PaginationState } from '@tanstack/react-table'
import { type ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns/format'
import { useUnit } from 'effector-react'

import ChatOutlineIcon from '@/assets/icons/icon-chat-outline.svg'
import CheckMarkIcon from '@/assets/icons/icon-checkmark.svg'
import RightChevronIcon from '@/assets/icons/icon-chevron-right.svg'
import OnlyFansIcon from '@/assets/icons/icon-only-fans.svg'
import XIcon from '@/assets/icons/icon-x.svg'
import { CreatorBrowserLink } from '@/components/ui/CreatorBrowserLink.tsx'
import { getFormattedDateRange } from '@/components/ui/export'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable.tsx'
import { $creatorId } from '@/models/creators.ts'
import { exportDataFromServer } from '@/utils/export/export.ts'

import { usePpvTracking } from '../../hooks/usePpvTracking'
import type { PpvMessage } from '../../types'
import { PpvTrackingQueryPanel } from '../PpvTrackingQueryPanel'

interface PpvMessagesTableProps {
  totalMessages: number
  messages: PpvMessage[]
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  showCents?: boolean
  categoryIndex: string
  mediaSetIndex: string
  refetchCurrentPage: () => void
}

export const PpvMessagesTable = ({
  pagination,
  setPagination,
  showCents,
  categoryIndex,
  mediaSetIndex,
  refetchCurrentPage,
  messages,
  totalMessages,
}: PpvMessagesTableProps) => {
  const creatorId = useUnit($creatorId)
  const { date } = usePpvTracking()
  const columns: ColumnDef<PpvMessage>[] = [
    {
      header: 'Message',
      accessorKey: 'content',
      enableSorting: true,
      sortingFn: 'text',
      cell: ({ getValue }) => (
        <div className="flex items-center gap-2.5">
          <ChatOutlineIcon className="fill-secondary-500 dark:fill-white" />
          <div className="... w-[200px] truncate xl:w-[300px]">
            {getValue() as string}
          </div>
        </div>
      ),
    },
    {
      header: 'Sent',
      accessorKey: 'sentDate',
      enableSorting: true,
      sortingFn: 'text',
      cell: ({ getValue }) => (
        <div className="whitespace-nowrap">
          {(getValue() as string)?.split('T')[0]}
        </div>
      ),
    },
    {
      header: 'Purchased',
      accessorKey: 'purchaseDate',
      enableSorting: true,
      sortingFn: 'text',
      cell: ({ getValue }) => (
        <div>
          {(getValue() as string) ? (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <CheckMarkIcon className="fill-success-default" />
              {(getValue() as string)?.split('T')[0]}
            </div>
          ) : (
            <XIcon className="fill-danger-default" />
          )}
        </div>
      ),
    },
    {
      header: 'Sender',
      accessorKey: 'browserName',
      enableSorting: true,
      sortingFn: 'text',
      cell: ({ getValue }) => <div>{getValue() as string}</div>,
    },
    {
      header: 'Price',
      accessorKey: 'netRevenue',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => (
        <div>{getCurrency(getValue() as string, showCents ? 2 : 0)}</div>
      ),
    },
    {
      id: 'action',
      header: () => <span className="w-full text-right">Action</span>,
      cell: (props) => (
        <div className="float-right">
          <CreatorBrowserLink
            href={`https://onlyfans.com/my/chats/chat/${props.row.original.fanId}?firstId=${props.row.original.messageId}`}
          >
            <a
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-primary-500"
            >
              <OnlyFansIcon className="fill-primary-500" />
              View in Chat
              <RightChevronIcon className="fill-primary-500" />
            </a>
          </CreatorBrowserLink>
        </div>
      ),
    },
  ]

  const downloadSheet = () => {
    if (!creatorId || !date.from || !date.to) return Promise.resolve()

    return exportDataFromServer(creatorId, {
      type: 'PpvTracking',
      filename: `PPV Messages, ${getFormattedDateRange(date)}`,
      fields: {
        from: format(date.from, 'yyyy-MM-dd'),
        to: format(date.to, 'yyyy-MM-dd'),
        mediaSetIndex,
        categoryIndex,
      },
    })
  }

  return (
    <DataTable
      enablePagination
      enableSorting
      enableExport
      exportOptions={{ getFromServer: downloadSheet }}
      extra={
        <PpvTrackingQueryPanel
          refetch={() => {
            refetchCurrentPage()
          }}
        />
      }
      title={`Found ${messages.length} Messages`}
      titleClassName="text-3xl"
      columns={columns}
      data={messages}
      pageCount={Math.ceil(totalMessages / pagination.pageSize)}
      paginationState={pagination}
      onPaginationChange={setPagination}
      className="px-6 py-0"
      wrapperClassName="gap-2"
    />
  )
}
